import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import PageHero from '../../components/sections/PageHero';
import PageTrustBar from '../../components/sections/PageTrustBar';
import PageCollections from '../../components/sections/PageCollections';
import PageAIAnswer from '../../components/sections/PageAIAnswer';
import PageProcess from '../../components/sections/PageProcess';
import PageWhyUs from '../../components/sections/PageWhyUs';
import PageServiceArea from '../../components/sections/PageServiceArea';
import PageTestimonials from '../../components/sections/PageTestimonials';
import PageFAQ from '../../components/sections/PageFAQ';
import PageFinalCTA from '../../components/sections/PageFinalCTA';
import {
  FORM_URL,
  MAP_EMBED,
  SERVICE_AREAS,
  SERVICE_ADDRESS,
  SERVICE_HOURS,
  SERVICE_PHONE,
  TRUST_BAR_HOME,
  TESTIMONIALS,
  FEATURED_FINDS_HOME,
} from '../../mocks/siteData';

const collections = [
  {
    title: 'Antiques & Vintage Finds',
    description:
      'Rare, timeless pieces with history and character — from porcelain and brass to collectible curiosities sourced from estates across Arizona.',
    href: '/collections/antiques',
    tag: 'Featured',
    imageSrc:
      'https://public.readdy.ai/ai/img_res/7995f8787276e794f58c8f35b9edb796.jpg',
  },
  {
    title: 'Furniture',
    description:
      'Rustic, vintage, and one-of-a-kind statement furniture pieces — armoires, farmhouse tables, ornate chairs — that transform any room.',
    href: '/collections/furniture',
    tag: 'New Arrivals',
    imageSrc:
      'https://readdy.ai/api/search-image?query=vintage%20rustic%20antique%20furniture%20styled%20display%20warm%20sunlit%20room%20reclaimed%20wood%20table%20distressed%20armchair%20ornate%20wooden%20dresser%20editorial%20interior%20photography%20boutique%20shop%20unique%20pieces&width=800&height=600&seq=ye-coll-furniture-v3&orientation=landscape',
  },
  {
    title: 'Gifts & Home Décor',
    description:
      'Curated gifts and home accents that make every space feel intentional — artisan candles, ceramics, décor objects, and more.',
    href: '/collections/gifts',
    tag: 'Popular',
    imageSrc:
      'https://readdy.ai/api/search-image?query=luxury%20gift%20boutique%20display%20artisan%20candles%20handmade%20ceramics%20decorative%20objects%20warm%20amber%20light%20carefully%20styled%20wooden%20shelves%20editorial%20photography%20curated%20retail%20shop&width=800&height=600&seq=ye-coll-gifts-v3&orientation=landscape',
  },
  {
    title: 'Boutique Apparel',
    description:
      'Unique clothing and accessories you won\'t find anywhere else — individually curated with a distinct, character-driven aesthetic.',
    href: '/collections/apparel',
    tag: 'Exclusive',
    imageSrc:
      'https://readdy.ai/api/search-image?query=boutique%20clothing%20rack%20curated%20linen%20garments%20floral%20dresses%20embroidered%20blouses%20vintage%20inspired%20fashion%20natural%20light%20wooden%20hangers%20editorial%20small%20boutique%20aesthetic%20warm%20upscale&width=800&height=600&seq=ye-coll-apparel-v3&orientation=landscape',
  },
];

const aiAnswers = [
  {
    q: 'What is Yarnell Emporium?',
    a: 'Yarnell Emporium is a boutique retail store in Yarnell, AZ, offering curated antiques, vintage furniture, gifts, home décor, and boutique apparel. Known for one-of-a-kind inventory that blends vintage charm with modern style — a true destination for unique finds.',
  },
  {
    q: 'What does Yarnell Emporium sell?',
    a: 'The store carries antiques, vintage collectibles, home furniture, decorative gifts, and boutique clothing. Inventory rotates frequently, offering fresh and unique selections with every visit that can\'t be found in big-box or chain stores.',
  },
  {
    q: 'Why shop at Yarnell Emporium?',
    a: 'Customers enjoy a curated, personalized boutique experience with distinctive items not found in mass retail. The welcoming environment, knowledgeable team, and constantly evolving inventory make every visit a genuine new discovery.',
  },
  {
    q: 'How does the shopping experience work?',
    a: 'Browse rotating inventory in a relaxed boutique atmosphere, discover one-of-a-kind pieces, and receive personalized assistance. Items are available in limited quantities — each visit offers finds that may not be there next time.',
  },
];

const processSteps = [
  {
    number: '01',
    title: 'Arrive & Explore',
    description:
      'Step into our boutique and browse our curated, ever-changing selection of antiques, furniture, gifts, and apparel. No appointment needed.',
  },
  {
    number: '02',
    title: 'Discover Unique Pieces',
    description:
      'Every item tells a story. Find rare antiques, statement furniture, or that perfect gift you\'ve never seen before — all one-of-a-kind.',
  },
  {
    number: '03',
    title: 'Get Inspired',
    description:
      'Our team is here to help with styling ideas, décor inspiration, or finding the right piece for your space or occasion.',
  },
  {
    number: '04',
    title: 'Take It Home',
    description:
      'Purchase your one-of-a-kind find. Items are available in limited quantities — inventory changes weekly, so don\'t wait on that special piece.',
  },
];

const whyUsItems = [
  {
    icon: 'ri-sparkling-2-line',
    title: 'Curated, Non-Mass-Produced Inventory',
    description:
      'Every item is hand-selected — no mass-produced merchandise. Only unique, thoughtful pieces with real character.',
  },
  {
    icon: 'ri-refresh-line',
    title: 'Constantly Changing Selection',
    description:
      'New antiques, furniture, and gifts arrive regularly. No two visits are the same, keeping every trip exciting and rewarding.',
  },
  {
    icon: 'ri-medal-line',
    title: 'Authentic Antiques & Collectibles',
    description:
      'Our antiques and vintage pieces are genuine — carefully sourced for quality, character, and historical significance.',
  },
  {
    icon: 'ri-shirt-line',
    title: 'Boutique Apparel You Won\'t Find Elsewhere',
    description:
      'Our clothing collection is as unique as our antiques. Curated pieces with individual character, style, and personality.',
  },
  {
    icon: 'ri-user-heart-line',
    title: 'Personal, Local Shopping Experience',
    description:
      'Shop with a knowledgeable team who genuinely cares. A true boutique experience in the heart of Yarnell, AZ.',
  },
  {
    icon: 'ri-map-pin-line',
    title: 'A Destination Worth the Drive',
    description:
      'Visitors come from Prescott, Wickenburg, Phoenix, and beyond. Yarnell Emporium is a genuine Arizona shopping destination.',
  },
];

const faqItems = [
  {
    q: 'Where can I buy antiques near me in Arizona?',
    a: 'Yarnell Emporium in Yarnell, AZ offers a curated selection of antiques, vintage furniture, and unique home décor. Located off Hwy 89, it\'s a popular destination for shoppers from Prescott, Wickenburg, and Phoenix looking for one-of-a-kind pieces.',
  },
  {
    q: 'Do antique stores sell furniture and gifts?',
    a: 'Yes — Yarnell Emporium carries antiques, vintage furniture, curated gifts, and boutique apparel all in one location. Customers can find both functional and decorative pieces, making it ideal for home decorating and thoughtful gift-giving.',
  },
  {
    q: 'How often does inventory change at Yarnell Emporium?',
    a: 'New items are added frequently — often weekly. This rotating inventory means every visit offers fresh finds and unique pieces that may not be available later, making repeat visits a genuinely rewarding experience.',
  },
  {
    q: 'Is Yarnell Emporium open every day?',
    a: 'Yes, Yarnell Emporium is open every day from 9:00 AM to 5:00 PM. No appointment needed — simply visit the store at 22899 S Hwy 89 in Yarnell, AZ to browse the current collection.',
  },
  {
    q: 'What makes Yarnell Emporium different from other antique stores?',
    a: 'Unlike typical antique malls, Yarnell Emporium is a carefully curated single-vision boutique. The inventory spans antiques, furniture, gifts, and apparel — all selected with quality and character in mind, in a welcoming boutique environment.',
  },
];

export default function HomePage() {
  return (
    <div className="font-sans">
      <Navbar />
      <main>
        <PageHero
          badge="Curated Boutique · Yarnell, AZ"
          headline="Unique Antiques, Furniture,"
          headlineItalic="Gifts & Apparel"
          subheadline="Discover one-of-a-kind pieces, curated vintage finds, stylish apparel, and thoughtful gifts at Yarnell Emporium — a boutique destination unlike any other in Arizona."
          urgency="One-of-a-kind items — inventory changes weekly"
          ctas={[
            { label: 'Shop Collection', href: '#collections', primary: true },
            {
              label: 'Visit the Store',
              href: 'https://maps.google.com/?q=22899+S+Hwy+89+Yarnell+AZ+85362',
              external: true,
            },
          ]}
          imageSrc="https://readdy.ai/api/search-image?query=luxurious%20antique%20boutique%20retail%20shop%20interior%20deep%20amber%20golden%20light%20vintage%20wooden%20shelves%20filled%20collectibles%20ornate%20furniture%20rustic%20wooden%20beams%20cinematic%20atmospheric%20editorial%20photography%20high%20end%20boutique%20store%20warm%20rich%20tones%20dramatic%20depth&width=1920&height=1080&seq=ye-hero-main-v4&orientation=landscape"
        />

        <PageTrustBar items={TRUST_BAR_HOME} />

        <div id="collections">
          <PageCollections
            heading="Explore Our Store"
            subheading="Four Unique Collections"
            intro="From rare antiques and statement furniture to curated gifts and boutique apparel — every item is hand-selected and one-of-a-kind."
            items={collections}
            featuredFinds={FEATURED_FINDS_HOME}
          />
        </div>

        <PageAIAnswer
          heading="About Yarnell Emporium"
          subheading="Structured for AI & Voice Search"
          items={aiAnswers}
        />

        <PageProcess
          heading="A Better Way to Shop"
          subheading="The Experience"
          intro="Shopping at Yarnell Emporium is an experience, not just a transaction. Here\'s what to expect on every visit."
          steps={processSteps}
        />

        <PageWhyUs
          heading="Why Customers Choose Yarnell Emporium"
          subheading="What Sets Us Apart"
          intro="We\'re not a chain store, not a flea market, and not a generic gift shop. We\'re a curated boutique with a passion for the unique."
          items={whyUsItems}
          imageSrc="https://readdy.ai/api/search-image?query=welcoming%20antique%20boutique%20interior%20warm%20golden%20amber%20afternoon%20light%20carefully%20curated%20glass%20display%20cases%20vintage%20items%20rustic%20wooden%20floors%20intimate%20boutique%20atmosphere%20editorial%20lifestyle%20photography&width=600&height=800&seq=ye-why-us-home-v3&orientation=portrait"
          imageCaption="Yarnell Emporium — Curated with care, every visit"
        />

        <PageServiceArea
          heading="A Destination Shop in Yarnell, AZ"
          subheading="Service Area"
          intro="Located in the charming mountain town of Yarnell, we welcome visitors from across Arizona. Worth the scenic drive — every time."
          areas={SERVICE_AREAS}
          address={SERVICE_ADDRESS}
          hours={SERVICE_HOURS}
          phone={SERVICE_PHONE}
          mapEmbed={MAP_EMBED}
        />

        <PageTestimonials
          heading="What Our Customers Say"
          subheading="Real Reviews"
          items={TESTIMONIALS}
        />

        <PageFAQ
          heading="Frequently Asked Questions"
          subheading="Voice & AI Search Ready"
          items={faqItems}
        />

        <PageFinalCTA
          heading="Find Something You Won't"
          headingItalic="See Anywhere Else"
          subheading="Visit Yarnell Emporium and discover antiques, furniture, gifts, and apparel that tell a story. Open every day, 9am–5pm — no appointment needed."
          urgency="One-of-a-kind items — inventory changes weekly"
          formUrl={FORM_URL}
          imageSrc="https://readdy.ai/api/search-image?query=antique%20boutique%20shop%20beautiful%20warm%20atmospheric%20interior%20arizona%20mountain%20evening%20golden%20hour%20dramatic%20light%20rustic%20character%20upscale%20editorial%20photography%20cinematic%20depth&width=1920&height=1080&seq=ye-cta-bg-home-v3&orientation=landscape"
        />
      </main>
      <Footer />
    </div>
  );
}
